import { useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, CreditCard, Plus, ExternalLink, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { SubscriptionInfo } from '@/types/auth/auth';

type SubscriptionFormData = {
  status: string;
  quantity: number;
  extra_quantity: number;
  stripe_id: string | null;
  trial_ends_at: string | null;
  ends_at: string | null;
};

interface AllSubscriptionsManagerProps {
  subscriptions: SubscriptionInfo[] | undefined;
  selectedSubscriptionId: number | null;
  subscriptionData: SubscriptionFormData;
  isCreatingNew: boolean;
  onSelectSubscription: (subscriptionId: number) => void;
  onFieldChange: (field: keyof SubscriptionFormData, value: string | number | null) => void;
  onStartTrial: () => void;
  onGiveFreeSubscription: () => void;
  onCreateNew: () => void;
  onOpenStripeSubscription?: (stripeId: string) => void;
  onDeleteSubscription?: (subscriptionId: number) => void;
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "canceled", label: "Canceled" },
  { value: "past_due", label: "Past Due" },
  { value: "incomplete", label: "Incomplete" },
  { value: "incomplete_expired", label: "Incomplete Expired" },
  { value: "unpaid", label: "Unpaid" },
];

const toLocalInput = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const fromLocalInput = (value: string) => (value ? new Date(value).toISOString() : null);

const FieldRow = ({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) => (
  <div className="grid gap-1.5 sm:grid-cols-[minmax(0,220px)_minmax(0,1fr)] sm:gap-6 sm:items-start">
    <div className="sm:pt-2.5">
      <Label htmlFor={id} className="text-sm font-semibold text-gray-700">
        {label}
      </Label>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

interface SubscriptionFieldsProps {
  idPrefix: string;
  subscriptionData: SubscriptionFormData;
  onFieldChange: AllSubscriptionsManagerProps["onFieldChange"];
  stripeManaged?: boolean;
  showExtraQuantity: boolean;
}

const SubscriptionFields = ({
  idPrefix,
  subscriptionData,
  onFieldChange,
  stripeManaged = false,
  showExtraQuantity,
}: SubscriptionFieldsProps) => (
  <div className="space-y-4">
    <FieldRow id={`${idPrefix}-status`} label="Status">
      <Select
        value={subscriptionData.status}
        onValueChange={(value) => onFieldChange("status", value)}
        disabled={stripeManaged}
      >
        <SelectTrigger id={`${idPrefix}-status`}>
          <SelectValue placeholder="Select status" />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldRow>

    <FieldRow id={`${idPrefix}-quantity`} label="Paid Agent Quantity" hint="Excluding the 1 free agent">
      <Input
        id={`${idPrefix}-quantity`}
        type="number"
        min="0"
        value={subscriptionData.quantity}
        onChange={(e) => onFieldChange("quantity", parseInt(e.target.value) || 0)}
        disabled={stripeManaged}
      />
    </FieldRow>

    <FieldRow id={`${idPrefix}-trial-ends`} label="Trial End Date" hint="Optional — empty if not on trial">
      <Input
        id={`${idPrefix}-trial-ends`}
        type="datetime-local"
        value={toLocalInput(subscriptionData.trial_ends_at)}
        onChange={(e) => onFieldChange("trial_ends_at", fromLocalInput(e.target.value))}
        disabled={stripeManaged}
      />
    </FieldRow>

    <FieldRow id={`${idPrefix}-ends-at`} label="Subscription End Date" hint="Optional — empty for indefinite">
      <Input
        id={`${idPrefix}-ends-at`}
        type="datetime-local"
        value={toLocalInput(subscriptionData.ends_at)}
        onChange={(e) => onFieldChange("ends_at", fromLocalInput(e.target.value))}
        disabled={stripeManaged}
      />
    </FieldRow>

    {showExtraQuantity && (
      <div className="pt-4 border-t border-gray-200">
        <FieldRow
          id={`${idPrefix}-extra-quantity`}
          label="Extra Quantity"
          hint="Admin override — extra agents on top of the plan, independent of Stripe"
        >
          <Input
            id={`${idPrefix}-extra-quantity`}
            type="number"
            min="0"
            value={subscriptionData.extra_quantity || ""}
            onChange={(e) => onFieldChange("extra_quantity", e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
          />
        </FieldRow>
      </div>
    )}
  </div>
);

export const AllSubscriptionsManager = ({
  subscriptions,
  selectedSubscriptionId,
  subscriptionData,
  isCreatingNew,
  onSelectSubscription,
  onFieldChange,
  onStartTrial,
  onGiveFreeSubscription,
  onCreateNew,
  onOpenStripeSubscription,
  onDeleteSubscription,
}: AllSubscriptionsManagerProps) => {
  const [expanded, setExpanded] = useState<number | null>(selectedSubscriptionId ?? subscriptions?.[0]?.id ?? null);

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "trialing":
        return "bg-blue-100 text-blue-800";
      case "canceled":
        return "bg-red-100 text-red-800";
      case "past_due":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between mb-[18px]">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <CreditCard className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold m-0">All Subscriptions</h2>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCreateNew}
          className="text-genie-primary border-genie-primary/30 hover:bg-genie-primary/10 gap-2"
        >
          <Plus className="h-4 w-4" />
          Create New
        </Button>
      </div>

      {selectedSubscriptionId === null && !isCreatingNew && (!subscriptions || subscriptions.length === 0) ? (
        <div className="rounded-md bg-gray-50 p-3">
          <p className="text-sm text-gray-900 font-medium">No subscriptions found</p>
          <p className="text-xs text-gray-500 mt-1">
            Create a manual trial or subscription for this user. This won't create billing in Stripe.
          </p>
          <div className="flex gap-2 mt-3">
            <Button type="button" variant="outline" size="sm" onClick={onStartTrial}>
              Start 30-Day Trial
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onGiveFreeSubscription}>
              Give Free Subscription
            </Button>
          </div>
        </div>
      ) : selectedSubscriptionId === null && (isCreatingNew || (subscriptions && subscriptions.length > 0)) ? (
        <>
          <div className="rounded-md bg-gray-50 p-3 mb-4">
            <p className="text-sm text-gray-900 font-medium">Creating New Subscription</p>
            <p className="text-xs text-gray-500 mt-1">Fill in the fields below and click Save Changes</p>
          </div>
          <div className="rounded-md p-4 bg-gray-50">
            <SubscriptionFields
              idPrefix="new"
              subscriptionData={subscriptionData}
              onFieldChange={onFieldChange}
              showExtraQuantity
            />
          </div>
        </>
      ) : (
        <div className="space-y-2">
          {subscriptions.map((sub) => (
            <div key={sub.id} className="rounded-md bg-gray-50 overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  onSelectSubscription(sub.id);
                  setExpanded(expanded === sub.id ? null : sub.id);
                }}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 text-left">
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded === sub.id ? "rotate-180" : ""}`} />
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      {sub.id === selectedSubscriptionId ? "Currently Editing" : `Subscription #${sub.id}`}
                    </p>
                    <p className="text-xs text-gray-500">Created {new Date(sub.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={getStatusColor(sub.status)}>{sub.status || "unknown"}</Badge>
                </div>
              </button>

              {expanded === sub.id && (
                <div className="px-3 pb-3 pt-2 border-t border-gray-200 space-y-4">
                  {sub.stripe_id && (
                    <div className="rounded-md bg-genie-primary/5 border border-genie-primary/20 p-3">
                      <p className="text-xs text-gray-700 mb-3">
                        <strong className="text-genie-primary">Stripe-Managed:</strong> Some fields are read-only. Manage in Stripe dashboard.
                      </p>
                      {onOpenStripeSubscription && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenStripeSubscription(sub.stripe_id!)}
                          className="text-genie-primary border-genie-primary/30 hover:bg-genie-primary/10 gap-2"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Manage in Stripe
                        </Button>
                      )}
                    </div>
                  )}

                  <SubscriptionFields
                    idPrefix={`sub-${sub.id}`}
                    subscriptionData={subscriptionData}
                    onFieldChange={onFieldChange}
                    stripeManaged={!!sub.stripe_id}
                    showExtraQuantity={sub.id === selectedSubscriptionId}
                  />

                  {sub.id === selectedSubscriptionId && onDeleteSubscription && (
                    <div className="pt-4 border-t border-gray-200">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteSubscription(sub.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Subscription
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {selectedSubscriptionId === null && subscriptions.length > 0 && (
            <div className="rounded-md bg-gray-50 p-3 mt-4">
              <p className="text-sm text-gray-900 font-medium">Shortcut Actions</p>
              <p className="text-xs text-gray-500 mt-1">Quick setup options for a new subscription</p>
              <div className="flex gap-2 mt-3">
                <Button type="button" variant="outline" size="sm" onClick={onStartTrial}>
                  Start 30-Day Trial
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onGiveFreeSubscription}>
                  Give Free Subscription
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
